import { useToast } from '@app/core/notifications';
import { useFragmentsViewCapture } from '@app/modules/fragments/hooks/useFragmentsViewCapture';
import { routeGenerator } from '@app/modules/fragments/routing/FragmentsRoutes';
import { useWebAppDeleteFragmentDetailById } from '@mi-front/api-v2';
import { Station, useRouter } from '@mi-front/core';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Fragment } from '../../types';

export interface FragmentDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  station: Station;
  fragment: Fragment;
}

/**
 * Confirmation dialog for deleting a fragment. Performs the mutation and redirects to the station list on success.
 */
export function FragmentDeleteDialog({
  open,
  onClose,
  station,
  fragment,
}: FragmentDeleteDialogProps) {
  const { history } = useRouter();
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const fragmentsCapture = useFragmentsViewCapture();
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteFragmentMutation = useWebAppDeleteFragmentDetailById();

  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteFragmentMutation.mutateAsync({ id: fragment.id });
      fragmentsCapture.delete({
        fragmentId: fragment.id,
        stationSlug: station.slug,
      });
      notifySuccess(
        $t({
          id: 'fragments.deleteScreen.deleted',
          defaultMessage: 'Fragmento eliminado',
        }),
      );
      onClose();
      history.replace(routeGenerator.list(station.slug));
    } catch (err: unknown) {
      notifyError(
        $t({
          id: 'fragments.deleteScreen.deleteError',
          defaultMessage: 'No se pudo eliminar el fragmento',
        }),
      );
      throw err;
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="fragments.deleteScreen.title" defaultMessage="Eliminar fragmento" />
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <FormattedMessage
            id="fragments.deleteScreen.prompt"
            defaultMessage={`
            ¿Deseas eliminar el fragmento <b>{title}</b>? {br}
            Esta acción no se puede deshacer.
          `}
            values={{
              title: fragment.title,
              br: <br />,
              b: (chunks: ReactNode) => <b>{chunks}</b>,
            }}
          />
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleClose} disabled={isDeleting}>
          <FormattedMessage id="common.actions.cancel" defaultMessage="Cancelar" />
        </Button>
        <LoadingButton
          onClick={handleDelete}
          loading={isDeleting}
          color="error"
          variant="contained"
        >
          <FormattedMessage id="common.actions.delete" defaultMessage="Eliminar" />
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
